/* eslint-disable react/no-danger */
import React, {useState} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {Modal, CircleSpinner} from '@wr/ui-patterns';
import logging from '@wr/logging';
import classNames from 'classnames';
import {getCsrf, getLanguageCode, getTranslations} from '../../../utils/selectors';
import {initPaymentSession} from '../../../actions/trueLayer/browser';
import assetUrl from '../../../utils/assetUrl';
import {genericErrorHasOccurred} from '../../../actions/generic-error/action-creators';
import {interpolateTranslation} from '../../../utils/interpolateTranslation';
import config from '../../../config';

const chainLinkIconUrl = assetUrl('images/chain-link.svg');
const WRIconUrl = assetUrl('images/logo-small.svg');
const logger = logging.createLogger(config.app.name, config.logger.logServerUrl);

function TrueLayerConfirmationModal({
    open,
    onClose,
    provider,
    paymentSessionId,
    csrfToken,
    dispatch,
    translations,
    languageCode
}) {
    const [
        initPaymentSessionRequestStarted,
        setInitPaymentSessionRequestStarted
    ] = useState(false);

    if (!provider) {
        return null;
    }

    const paymentSessionInitRequest = async () => {
        setInitPaymentSessionRequestStarted(true);
        try {
            const initResponseData = await initPaymentSession({
                paymentSessionId,
                provider,
                csrfToken,
                languageCode
            });
            window.location.assign(initResponseData.redirectUrl);
        } catch (err) {
            logger.error(err.message);
            setInitPaymentSessionRequestStarted(false);
            dispatch(genericErrorHasOccurred(true));
            onClose();
        }
    };

    // modal's height is not fixed
    // so visibility: hidden keeps height the same when spinner appears
    const hiddenClassIfRequestStarted = classNames({
        'visibility-hidden': initPaymentSessionRequestStarted
    });

    const onCloseWrapper = () => {
        if (!initPaymentSessionRequestStarted) {
            onClose();
        }
    };

    const title = interpolateTranslation(translations.trueLayer.payingFromTitle, {
        provider: provider.displayName
    });

    return (
        <Modal
            open={open}
            onClose={onCloseWrapper}
            ariaLabelledBy="confirmation-modal-title"
        >
            <div className="confirmation-modal">
                <h2 id="confirmation-modal-title">{title}</h2>
                <div className="confirmation-modal-content">
                    {initPaymentSessionRequestStarted && <CircleSpinner />}

                    <div className={hiddenClassIfRequestStarted}>
                        <div className="confirmation-modal-icon-row">
                            <img alt="" src={provider.iconUrl} />
                            <img
                                alt=""
                                src={chainLinkIconUrl}
                                className="chain-link-icon"
                            />
                            <img alt="" src={WRIconUrl} />
                        </div>
                        <p
                            dangerouslySetInnerHTML={{
                                __html: translations.trueLayer.termsOfService
                            }}
                        />
                        <p>{translations.trueLayer.bankDetailsConsent}</p>
                        <button
                            onClick={paymentSessionInitRequest}
                            className="ds-button-primary"
                            type="button"
                        >
                            {translations.trueLayer.confirm}
                        </button>
                        <button
                            type="button"
                            className="ds-button-tertiary"
                            onClick={onCloseWrapper}
                        >
                            {translations.common.cancel}
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
}

TrueLayerConfirmationModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    provider: PropTypes.shape({
        iconUrl: PropTypes.string.isRequired,
        displayName: PropTypes.string.isRequired
    }),
    paymentSessionId: PropTypes.string.isRequired,
    csrfToken: PropTypes.string.isRequired,
    dispatch: PropTypes.func.isRequired,
    translations: PropTypes.shape().isRequired,
    languageCode: PropTypes.string.isRequired
};

TrueLayerConfirmationModal.defaultProps = {
    provider: undefined
};

const mapStateToProps = state => ({
    csrfToken: getCsrf(state),
    translations: getTranslations(state),
    languageCode: getLanguageCode(state)
});

export default connect(mapStateToProps)(TrueLayerConfirmationModal);
